import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl, publicMediaSrc } from "../config/api";
import useReveal from "../hooks/useReveal";
import { formatIntlPhone } from "../utils/formatIntlPhone";
import { enquiryContextFromState, shouldAttachEnquiryPage, useEnquiryPageTitle, whatsappEnquiryHref } from "../utils/whatsappEnquiry";
import specificBg from "../assets/specific-bg.jpg";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";

const nav = [
  { to: "/", label: "Home" },
  { to: "/category", label: "Category" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About Us" },
  { to: "/deliveries", label: "Deliveries" },
  { to: "/contact", label: "Contact Us" },
];

function externalHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, "")}`;
}

function SocialIcon({ href, label, children }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="social-icon text-white/80 hover:text-white"
    >
      {children}
    </a>
  );
}

function scrollToPageTop() {
  window.scrollTo(0, 0);
}

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function Footer() {
  const ctaRef = useRef(null);
  const location = useLocation();
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");

  useReveal(ctaRef);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) {
          setContact(Array.isArray(list) && list[0] ? list[0] : null);
          setContactStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContact(null);
          setContactStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const logoSrc = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);
  const companyName = String(contact?.companyName || "").trim();
  const companyRegNo = String(contact?.companyRegNo || "").trim();
  const emailAddress = String(contact?.emailAddress || "").trim();
  const address = String(contact?.address || "").trim();
  const headingTitle = useEnquiryPageTitle(location.pathname);
  const fromState = enquiryContextFromState(location.state);
  const enquiryPath = fromState.path || location.pathname;
  const waHref = whatsappEnquiryHref({
    number: contact?.whatsappNumber,
    siteName: contact?.siteName,
    title: fromState.title || (shouldAttachEnquiryPage(enquiryPath) ? headingTitle : ""),
    path: enquiryPath,
  });
  const whatsappNumber = String(contact?.whatsappNumber || "").trim();
  const phoneDisplay = formatIntlPhone(whatsappNumber);
  const instagramHref = externalHref(contact?.instagram);
  const facebookHref = externalHref(contact?.facebook);
  const showFollowUs = Boolean(instagramHref || facebookHref);
  const whatsappReady = contactStatus !== "loading";

  return (
    <footer className="bg-at-darker text-white/70">
      <section ref={ctaRef} className="relative overflow-hidden text-white">
        <img src={specificBg} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className={`relative z-10 flex flex-col items-start justify-between gap-6 py-12 md:flex-row md:items-center ${PAGE_GUTTER}`}>
          <div data-reveal="">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40">Let&apos;s work together</p>
            <h2 className="mt-2 font-serif text-3xl uppercase">Need something specific?</h2>
            <p className="mt-2 max-w-lg text-sm text-white/65">
              Tell us what you&apos;re sourcing and our team will help you find the right solution.
            </p>
          </div>
          <div className="flex flex-col items-center gap-6" data-reveal="" data-reveal-delay="1">
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/contact?intent=quote#contact-form"
                onClick={lockDoubleClick}
                className="cta-btn rounded bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
              >
                Get a Quote <span className="btn-arrow">→</span>
              </Link>
              {!whatsappReady ? (
                <span
                  className="cta-btn inline-flex cursor-not-allowed items-center gap-2 rounded border border-white/30 px-5 py-2.5 text-sm font-semibold text-white opacity-60"
                  aria-disabled="true"
                  aria-busy="true"
                >
                  WhatsApp Us
                </span>
              ) : waHref ? (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={lockDoubleClick}
                  className="cta-btn inline-flex items-center gap-2 rounded border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
                >
                  WhatsApp Us
                </a>
              ) : (
                <Link
                  to="/contact"
                  onClick={(event) => {
                    lockDoubleClick(event);
                    scrollToPageTop();
                  }}
                  className="cta-btn inline-flex items-center gap-2 rounded border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
                >
                  WhatsApp Us
                </Link>
              )}
            </div>
            <ul className="flex flex-wrap gap-6 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/80 sm:gap-8">
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.supply)} alt="" className="h-6 w-6 object-contain" />
                Fast Response
              </li>
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.quality)} alt="" className="h-6 w-6 object-contain" />
                Reliable Support
              </li>
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.trusted)} alt="" className="h-6 w-6 object-contain" />
                Trusted Partner
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className={`grid gap-10 py-12 md:grid-cols-2 ${showFollowUs ? "lg:grid-cols-4" : "lg:grid-cols-3"} ${PAGE_GUTTER}`}>
        <div>
          <Link to="/" onClick={scrollToPageTop} className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt={companyName ? "" : "Home"}
              className="h-12 w-12 object-contain invert"
            />
            <span className="min-w-0">
              {companyName ? (
                <span className="block text-sm font-semibold uppercase leading-tight tracking-[0.08em] text-white">
                  {companyName}
                </span>
              ) : null}
              {companyRegNo ? (
                <span className="mt-1 block text-[10px] leading-none text-white/40">
                  {companyRegNo}
                </span>
              ) : null}
            </span>
          </Link>
          <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.16em] text-white/45">
            Steel · Hardware · Paint · Roofing
          </p>
          <span className="mt-4 block h-px w-12 bg-at-gold" />
          <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/80">
            Building Industry Together
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 md:contents">
        <div>
          <p className="text-sm font-semibold text-white">Quick Links</p>
          <nav aria-label="Footer" className="mt-4 flex flex-col gap-2 text-sm text-white/65">
            {nav.map((item) => (
              <Link key={item.to} to={item.to} onClick={scrollToPageTop} className="hover:text-white">
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div>
          <p className="text-sm font-semibold text-white">Contact Us</p>
          <ul className="mt-4 flex flex-col gap-3 text-sm text-white/65">
            {whatsappNumber ? (
              <li className="flex items-start gap-2">
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <path d="M6.6 3.8h2.2l1.3 3.2-1.4 1.4a14 14 0 006.9 6.9l1.4-1.4 3.2 1.3v2.2A2 2 0 0118 19.2 15.2 15.2 0 014.8 6a2 2 0 011.8-2.2z" />
                </svg>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                  {phoneDisplay}
                </a>
              </li>
            ) : null}
            {emailAddress ? (
              <li className="flex items-start gap-2">
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 7 9-7" />
                </svg>
                <a href={`mailto:${emailAddress}`} className="break-all hover:text-white">
                  {emailAddress}
                </a>
              </li>
            ) : null}
            {address ? (
              <li className="flex items-start gap-2">
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
                  <circle cx="12" cy="10" r="2.2" />
                </svg>
                <span className="whitespace-pre-line">{address}</span>
              </li>
            ) : null}
          </ul>
        </div>
        </div>

        {showFollowUs ? (
          <div>
            <p className="text-sm font-semibold text-white">Follow Us</p>
            <div className="mt-4 flex items-center gap-4">
              <SocialIcon href={instagramHref} label="Instagram">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M7 3h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7a4 4 0 014-4zm10 1.8H7A2.2 2.2 0 004.8 7v10A2.2 2.2 0 007 19.2h10a2.2 2.2 0 002.2-2.2V7A2.2 2.2 0 0017 4.8zM12 8.2A3.8 3.8 0 1112 15.8 3.8 3.8 0 0112 8.2zm0 1.7a2.1 2.1 0 100 4.2 2.1 2.1 0 000-4.2zM17.35 6.4a1 1 0 110 2 1 1 0 010-2z" />
                </svg>
              </SocialIcon>
              <SocialIcon href={facebookHref} label="Facebook">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M14.5 9H16V6.5h-1.5c-2 0-3.3 1.2-3.3 3.2V11H10v2.4h1.2V21h2.6v-7.6H16L16.5 11h-2.7V9.8c0-.5.3-.8.7-.8z" />
                </svg>
              </SocialIcon>
            </div>
          </div>
        ) : null}
      </div>

      <div className={`flex flex-col gap-3 border-t border-white/10 py-5 sm:flex-row sm:items-center sm:justify-between ${PAGE_GUTTER}`}>
        <p className="text-[11px] text-white/40">
          © {new Date().getFullYear()} {companyName || "AT Industrial Supply"}. All rights reserved.
        </p>
        <p className="flex items-center gap-3 text-[11px] text-white/40">
          <span className="hidden h-px w-6 bg-at-gold sm:inline-block" />
          Quality Materials. Stronger Tomorrow.
        </p>
      </div>
    </footer>
  );
}

export default Footer;
